import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search, User } from 'lucide-react';
import type { User as AppUser } from '../../../../types/user';
import { twMerge } from 'tailwind-merge';

function userKey(u: AppUser): string {
  return String(u._id ?? u.id ?? '');
}

function userInitials(name: string | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return '?';
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  return n.slice(0, 2).toUpperCase();
}

/* Sovereign Emerald avatar inks — engraved greens + champagne, never neon. */
const AVATAR_COLORS = [
  'from-[#1F5A47] to-[#0B3B2E]',
  'from-[#339274] to-[#1F5A47]',
  'from-[#264f3c] to-[#0B3B2E]',
  'from-[#C9A961] to-[#8A5A08]',
  'from-[#0B3B2E] to-[#0B3B2E]',
  'from-[#2d5c47] to-[#1F5A47]',
];

function getAvatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? AVATAR_COLORS[0]!;
}

export function EpcTeamUserPicker({
  users,
  value,
  onChange,
  disabled,
  placeholder = 'Search users by name or email…',
  excludeUserIds,
  roleLabelByUserId,
  emptyHint,
}: {
  users: AppUser[];
  value: string;
  onChange: (userId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  excludeUserIds?: Set<string>;
  /** Optional per-user EPC role label rendered below the name (e.g. "Site Engineer"). */
  roleLabelByUserId?: Map<string, string | undefined>;
  /** Replaces the default "no users found" message — useful when the directory has been pre-filtered. */
  emptyHint?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return users.filter((u) => {
      const id = userKey(u);
      if (!id || excludeUserIds?.has(id)) return false;
      if (!qq) return true;
      const name = (u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const code = (u.code || '').toLowerCase();
      return name.includes(qq) || email.includes(qq) || code.includes(qq);
    });
  }, [users, q, excludeUserIds]);

  const selected = value ? users.find((u) => userKey(u) === value) : undefined;

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={twMerge(
          'flex w-full items-center gap-3 rounded-xl border bg-[#fcfaf3] px-3 py-2.5 text-left text-sm transition-all',
          open
            ? 'border-[#0B3B2E] ring-[3px] ring-[rgba(166,206,57,0.22)] shadow-sm'
            : 'border-[rgba(11,59,46,0.28)] hover:border-[rgba(51,146,116,0.6)] hover:shadow-sm',
          disabled ? 'cursor-not-allowed opacity-60 bg-[#F2EADB]' : ''
        )}
      >
        {selected ? (
          <>
            <div
              className={twMerge(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-[10px] font-black text-white shadow-sm',
                getAvatarColor(userKey(selected))
              )}
              aria-hidden
            >
              {userInitials(selected.name)}
            </div>
            <div className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-[#0B3B2E]">{selected.name}</span>
              {selected.email ? (
                <span className="block truncate text-xs text-[#7C8A82]">{selected.email}</span>
              ) : null}
            </div>
            <Check className="h-4 w-4 shrink-0 text-[#339274]" />
          </>
        ) : (
          <>
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[rgba(11,59,46,0.20)] bg-[rgba(201,169,97,0.14)]"
              aria-hidden
            >
              <User className="h-3.5 w-3.5 text-[#0B3B2E]" />
            </div>
            <span className="flex-1 text-sm text-[#7C8A82]">{placeholder}</span>
            <ChevronDown
              className={twMerge('h-4 w-4 shrink-0 text-[#7C8A82] transition-transform', open && 'rotate-180')}
            />
          </>
        )}
      </button>

      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-label="Close picker"
            onClick={() => { setOpen(false); setQ(''); }}
          />
          <div className="absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden rounded-2xl border border-[rgba(11,59,46,0.18)] bg-[rgba(255,254,251,0.98)] shadow-[0_1px_2px_rgba(11,59,46,0.04),0_16px_40px_-24px_rgba(11,59,46,0.20)] ring-1 ring-[rgba(11,59,46,0.08)]">
            {/* Search input */}
            <div className="flex items-center gap-2 border-b border-[rgba(11,59,46,0.12)] px-3 py-2.5 bg-[#F8F2E2]">
              <Search className="h-4 w-4 shrink-0 text-[#7C8A82]" aria-hidden />
              <input
                autoFocus
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[#7C8A82] text-[#2A1F17]"
                placeholder="Search by name or email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {q ? (
                <button
                  type="button"
                  className="rounded-md p-0.5 text-[#7C8A82] hover:bg-[rgba(11,59,46,0.08)] hover:text-[#0B3B2E]"
                  onClick={() => setQ('')}
                >
                  ✕
                </button>
              ) : null}
            </div>

            {/* Count */}
            {filtered.length > 0 ? (
              <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#7C8A82] bg-[rgba(248,242,226,0.7)]">
                {filtered.length} user{filtered.length !== 1 ? 's' : ''}
              </p>
            ) : null}

            {/* List */}
            <ul className="max-h-60 overflow-auto">
              {filtered.slice(0, 80).map((u) => {
                const id = userKey(u);
                const isSelected = id === value;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={twMerge(
                        'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors',
                        isSelected ? 'bg-[rgba(51,146,116,0.10)]' : 'hover:bg-[rgba(11,59,46,0.05)]'
                      )}
                      onClick={() => {
                        onChange(id);
                        setOpen(false);
                        setQ('');
                      }}
                    >
                      <div
                        className={twMerge(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-[10px] font-black text-white shadow-sm',
                          getAvatarColor(id)
                        )}
                        aria-hidden
                      >
                        {userInitials(u.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-[#0B3B2E]">{u.name}</p>
                        <p className="truncate text-xs text-[#7C8A82]">{u.email || u.mobile || id}</p>
                        {roleLabelByUserId?.get(id) ? (
                          <p className="mt-0.5 inline-flex items-center gap-1 rounded-md bg-[#F8F2E2] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#1F5A47] ring-1 ring-[rgba(11,59,46,0.18)]">
                            {roleLabelByUserId.get(id)}
                          </p>
                        ) : null}
                      </div>
                      {isSelected ? <Check className="h-4 w-4 shrink-0 text-[#339274]" /> : null}
                    </button>
                  </li>
                );
              })}
              {filtered.length === 0 ? (
                <li className="flex flex-col items-center justify-center gap-2 px-4 py-8 text-center">
                  <User className="h-8 w-8 text-[rgba(11,59,46,0.18)]" />
                  <p className="text-sm font-medium text-[#2A1F17]">No matching users found</p>
                  <p className="text-xs text-[#7C8A82]">{emptyHint ?? 'Try a different search term'}</p>
                </li>
              ) : null}
            </ul>
          </div>
        </>
      ) : null}
    </div>
  );
}
